import React from 'react';
import classes from 'components/Settings/Components/UserProfile/components/ChangePassword/ChangePasswordModal/changePasswordModal.module.scss';
import { Controller, useForm } from 'react-hook-form';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import { CustomizedButton } from 'components/common';
import { useQueryPasswordPatternData } from 'api/queries/passwordPatternDataQuery';
import useUserHook from 'hooks/useUserHook';
import { encrypt } from 'components/App/AuthPublicPages/Login/login.utils';
import { useMutationChangePassword } from 'components/Settings/Components/UsersManagement/queries/useQueryUsersManagement';
import { PasswordInput } from 'components/common/passwordInput/passwordInput';
import { useQueryServerTime } from 'api/queries/serverTime.query';

type Props = {
    onClose: () => void;
    userEmail: string;
};

interface IChangePassword {
    currentPassword: string;
    newPassword: string;
    password_repeat: string;
}
export const ChangePasswordModal = ({ onClose, userEmail }: Props) => {
    const { ability, email } = useUserHook();
    const { mutate } = useMutationChangePassword();
    const isAdmin = ability.can('all', 'Admin');
    const {
        formState: { errors },
        handleSubmit,
        watch,
        control,
    } = useForm<IChangePassword>();
    const { handleSnackBar } = useCustomSnackBar();
    const { data: patternData } = useQueryPasswordPatternData({ email });
    const { data: serverTime } = useQueryServerTime();

    const onSubmit = (data) => {
        const params = {
            currentPassword: data.currentPassword,
            newPassword: data.newPassword,
            userName: userEmail,
        };
        const time = serverTime?.time as number;
        const payload = time !== 0 ? { encrypt: encrypt(JSON.stringify(params), time), time } : params;
        mutate(payload, {
            onSuccess: () => {
                handleSnackBar('Successfully saved!', 'success');
                onClose();
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
        });
    };

    return (
        <div className={classes.ChangePasswordContainer}>
            <div className={classes.changePasswordTitle}>Change password</div>
            <div className={classes.changePasswordSubTitle}>
                {patternData ? patternData.message : 'Choose a unique password'}
            </div>
            <form
                onSubmit={(e) => {
                    e.stopPropagation();
                    return handleSubmit(onSubmit)(e);
                }}
            >
                <div className={classes.inputWrapperContainer}>
                    {!isAdmin && (
                        <div className={classes.inputWrapper}>
                            <Controller
                                render={({ field }) => (
                                    <PasswordInput
                                        {...field}
                                        placeholder="Type your current password*"
                                        id="changePassword_currentPassword"
                                        className={`${errors.currentPassword && classes.inValidField}`}
                                    />
                                )}
                                name={`currentPassword`}
                                control={control}
                                rules={{
                                    required: 'You must specify your old password',
                                }}
                                defaultValue={''}
                            />
                        </div>
                    )}

                    <div className={classes.inputWrapper}>
                        <Controller
                            render={({ field }) => (
                                <PasswordInput
                                    {...field}
                                    placeholder="Type your new password*"
                                    id="changePassword_newPassword"
                                    className={`${errors.newPassword && classes.inValidField}`}
                                />
                            )}
                            name={`newPassword`}
                            control={control}
                            rules={{
                                required: 'You must specify your new password',
                                pattern: {
                                    value: new RegExp(patternData?.pattern),
                                    message: `${patternData?.message}. Please try again`,
                                },
                            }}
                            defaultValue={''}
                        />
                    </div>

                    <div className={classes.inputWrapper}>
                        <Controller
                            render={({ field }) => (
                                <PasswordInput
                                    {...field}
                                    placeholder="Retype your new password*"
                                    id="changePassword_repeatPassword"
                                    className={`${errors.password_repeat && classes.inValidField}`}
                                />
                            )}
                            name={`password_repeat`}
                            control={control}
                            rules={{
                                required: 'You must retype your new password',
                                pattern: {
                                    value: new RegExp(patternData?.pattern),
                                    message: `${patternData?.message}. Please try again`,
                                },
                                validate: (value) => value === watch('newPassword') || 'The passwords do not match',
                            }}
                            defaultValue={''}
                        />
                    </div>
                </div>

                <CustomizedButton size={'large'} color={'primary'} type="submit" id="changePassword_saveBtn">
                    Save
                </CustomizedButton>

                <div className={classes.errorContainer}>
                    {errors.currentPassword && <p>{errors.currentPassword.message} </p>}
                    {errors.newPassword && <p>{errors.newPassword.message}</p>}
                    {errors.password_repeat && <p>{errors.password_repeat.message}</p>}
                </div>
            </form>
        </div>
    );
};
